/**
 * Photo uploads from phones: camera photos are 3–12 MB, which can take well over the API's
 * default 10s timeout on mobile data. Shrink them in the browser first and allow longer uploads.
 */

export const PHOTO_UPLOAD_TIMEOUT_MS = 120000;
export const PHOTO_MAX_DIMENSION = 2000;
const SHRINK_ABOVE_BYTES = 600 * 1024;
const JPEG_QUALITY = 0.82;
const SKIP_TYPES = ['image/gif', 'image/svg+xml'];

function loadBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file, { imageOrientation: 'from-image' });
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });
}

/**
 * Resize a large photo to at most PHOTO_MAX_DIMENSION px (JPEG). Returns the original file when
 * it is small, not a resizable image, or the browser can't decode it.
 * @param {File} file
 * @returns {Promise<File>}
 */
export async function shrinkPhotoForUpload(file) {
  if (!file || !String(file.type || '').startsWith('image/') || SKIP_TYPES.includes(file.type)) return file;
  if (file.size <= SHRINK_ABOVE_BYTES) return file;
  try {
    const image = await loadBitmap(file);
    const width = image.width || image.naturalWidth;
    const height = image.height || image.naturalHeight;
    if (!width || !height) return file;
    const scale = Math.min(1, PHOTO_MAX_DIMENSION / Math.max(width, height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    if (typeof image.close === 'function') image.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob || blob.size >= file.size) return file;
    const name = `${String(file.name || 'photo').replace(/\.[^.]+$/, '')}.jpg`;
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (_) {
    return file;
  }
}

/** Shrink `file` and build the multipart body for a photo upload. */
export async function photoFormData(file, fields = {}) {
  const formData = new FormData();
  formData.append('photo', await shrinkPhotoForUpload(file));
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== undefined && value !== null) formData.append(key, value);
  });
  return formData;
}

/** User-facing message for a failed upload. */
export function photoUploadErrorMessage(err, fallback = 'Failed to upload photo') {
  if (err?.response?.data?.error) return err.response.data.error;
  if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
    return 'The photo upload took too long. Check your connection and try again.';
  }
  if (!err?.response) return 'The photo could not be sent. Check your connection and try again.';
  return fallback;
}
